import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle, AlertCircle } from 'lucide-react';
import {
  CogIcon,
  UsersIcon,
  DatabaseIcon,
  KeyIcon,
  ServerIcon,
  InfoIcon,
  Save,
  RefreshCw,
  HelpCircle,
  HeartPulse,
  Trash2
} from 'lucide-react';

import UserManagement from './UserManagement';
import SystemHealthPanel from './HealthPanel';

const ConfigurationInterface: React.FC = () => {
  // State management
  const [activeTab, setActiveTab] = useState("users");
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState(false);
  const { toast } = useToast();

  // Example configuration state - replace with your actual configuration
  const [config, setConfig] = useState({
    chunking: {
      min_chunk_size: 100,
      max_chunk_size: 1500,
      overlap: 150,
      chunking_strategy: "algorithmic",
    },
    classification: {
      threshold: 0.7,
      monitoring_window: 7,
      batch_size: 10,
    },
    system: {
      enable_monitoring: true,
      debug_mode: false,
      max_workers: 4,
      processing_timeout: 300,
    },
    default_model: "claude-3-7-sonnet-20250219",
    api_url: "https://s.antz.is",
    environment: "staging",
  });

  // Save configuration handler
  const handleSaveConfig = async (section: string) => {
    setIsLoading(true);

    try {
      // Mock API call - replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      toast({
        title: "Configuration Saved",
        description: `${section} settings have been updated successfully.`,
      });

      // Show success animation
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save configuration. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Reset configuration handler
  const handleResetConfig = (section: string) => {
    // Replace with actual reset logic
    toast({
      title: "Configuration Reset",
      description: `${section} settings have been reset to defaults.`,
    });
  };

  // Data reset handler
  const handleDataReset = async () => {
    setIsLoading(true);
    setResetError(null);
    setResetSuccess(false);

    try {
      const response = await fetch('/api/v1/config/clear-all-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to reset data');
      }

      setResetSuccess(true);
      setIsResetDialogOpen(false);
      toast({
        title: "Success",
        description: "All system data has been reset successfully.",
      });
    } catch (error) {
      setResetError(error instanceof Error ? error.message : 'An unknown error occurred');
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : 'Failed to reset data',
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Tab change handler with animation
  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  return (
    <div className="container mx-auto p-6 space-y-6 max-w-6xl">
      <div className="flex flex-col space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">System Configuration</h1>
        <p className="text-muted-foreground">
          Manage application settings, user access, and system parameters.
        </p>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="w-full"
      >
        <div className="border rounded-lg p-1 mb-6">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-6 lg:grid-cols-7">
            <TabsTrigger value="users" className="flex items-center gap-2">
              <UsersIcon className="h-4 w-4" />
              <span className="hidden md:inline">Users</span>
            </TabsTrigger>
            <TabsTrigger value="chunking" className="flex items-center gap-2">
              <DatabaseIcon className="h-4 w-4" />
              <span className="hidden md:inline">Chunking</span>
            </TabsTrigger>
            <TabsTrigger value="classification" className="flex items-center gap-2">
              <CogIcon className="h-4 w-4" />
              <span className="hidden md:inline">Classification</span>
            </TabsTrigger>
            <TabsTrigger value="ai" className="flex items-center gap-2">
              <ServerIcon className="h-4 w-4" />
              <span className="hidden md:inline">AI Models</span>
            </TabsTrigger>
            <TabsTrigger value="security" className="flex items-center gap-2">
              <KeyIcon className="h-4 w-4" />
              <span className="hidden md:inline">Security</span>
            </TabsTrigger>
            <TabsTrigger value="system" className="flex items-center gap-2">
              <InfoIcon className="h-4 w-4" />
              <span className="hidden md:inline">System</span>
            </TabsTrigger>
            <TabsTrigger value="health" className="flex items-center gap-2">
              <HeartPulse className="h-4 w-4" />
              <span className="hidden md:inline">Health</span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* User Management Tab */}
        <TabsContent value="users" className="space-y-4 animate-in fade-in-50">
          <UserManagement />
        </TabsContent>

        {/* Chunking Configuration Tab */}
        <TabsContent value="chunking" className="space-y-4 animate-in fade-in-50">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Document Chunking Configuration</CardTitle>
                  <CardDescription>
                    Configure how documents are split for processing and embedding.
                  </CardDescription>
                </div>
                <Badge variant="outline" className="ml-auto">
                  {config.environment}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="min_chunk_size">Minimum Chunk Size</Label>
                      <span className="text-sm text-muted-foreground">{config.chunking.min_chunk_size} characters</span>
                    </div>
                    <Slider
                      id="min_chunk_size"
                      min={50}
                      max={500}
                      step={10}
                      value={[config.chunking.min_chunk_size]}
                      onValueChange={(value) => setConfig({
                        ...config,
                        chunking: { ...config.chunking, min_chunk_size: value[0] }
                      })}
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="max_chunk_size">Maximum Chunk Size</Label>
                      <span className="text-sm text-muted-foreground">{config.chunking.max_chunk_size} characters</span>
                    </div>
                    <Slider
                      id="max_chunk_size"
                      min={500}
                      max={5000}
                      step={100}
                      value={[config.chunking.max_chunk_size]}
                      onValueChange={(value) => setConfig({
                        ...config,
                        chunking: { ...config.chunking, max_chunk_size: value[0] }
                      })}
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="overlap">Chunk Overlap</Label>
                      <span className="text-sm text-muted-foreground">{config.chunking.overlap} characters</span>
                    </div>
                    <Slider
                      id="overlap"
                      min={0}
                      max={500}
                      step={10}
                      value={[config.chunking.overlap]}
                      onValueChange={(value) => setConfig({
                        ...config,
                        chunking: { ...config.chunking, overlap: value[0] }
                      })}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="chunking_strategy">Chunking Strategy</Label>
                    <Select
                      value={config.chunking.chunking_strategy}
                      onValueChange={(value) => setConfig({
                        ...config,
                        chunking: { ...config.chunking, chunking_strategy: value }
                      })}
                    >
                      <SelectTrigger id="chunking_strategy">
                        <SelectValue placeholder="Select a strategy" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="paragraph">Paragraph</SelectItem>
                        <SelectItem value="sentence">Sentence</SelectItem>
                        <SelectItem value="fixed_size">Fixed Size</SelectItem>
                        <SelectItem value="recursive">Recursive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2 pt-6">
                    <div className="flex items-center space-x-2">
                      <Switch id="respect_sections" defaultChecked />
                      <Label htmlFor="respect_sections">Respect document sections</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-4 w-4 text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="w-80">When enabled, the chunker will try to keep content from the same section together.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Switch id="preserve_tables" defaultChecked />
                      <Label htmlFor="preserve_tables">Preserve tables</Label>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Switch id="preserve_lists" defaultChecked />
                      <Label htmlFor="preserve_lists">Preserve lists</Label>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline" onClick={() => handleResetConfig('Chunking')} disabled={isLoading}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Reset Defaults
              </Button>
              <Button onClick={() => handleSaveConfig('Chunking')} disabled={isLoading}>
                {isLoading ? (
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                ) : showSuccess ? (
                  <CheckCircle className="mr-2 h-4 w-4 text-green-500" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Changes
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        {/* Classification Configuration Tab */}
        <TabsContent value="classification" className="space-y-4 animate-in fade-in-50">
          <Card>
            <CardHeader>
              <CardTitle>Document Classification Settings</CardTitle>
              <CardDescription>
                Configure parameters for automatic document classification.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="threshold">Classification Threshold</Label>
                      <span className="text-sm text-muted-foreground">{config.classification.threshold}</span>
                    </div>
                    <Slider
                      id="threshold"
                      min={0.1}
                      max={1.0}
                      step={0.05}
                      value={[config.classification.threshold]}
                      onValueChange={(value) => setConfig({
                        ...config,
                        classification: { ...config.classification, threshold: value[0] }
                      })}
                    />
                    <p className="text-sm text-muted-foreground">
                      Minimum confidence score required for classification to be applied.
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="monitoring_window">Monitoring Window (days)</Label>
                    <Input
                      id="monitoring_window"
                      type="number"
                      value={config.classification.monitoring_window}
                      onChange={(e) => setConfig({
                        ...config,
                        classification: { ...config.classification, monitoring_window: parseInt(e.target.value) }
                      })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="batch_size">Processing Batch Size</Label>
                    <Input
                      id="batch_size"
                      type="number"
                      value={config.classification.batch_size}
                      onChange={(e) => setConfig({
                        ...config,
                        classification: { ...config.classification, batch_size: parseInt(e.target.value) }
                      })}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline" onClick={() => handleResetConfig('Classification')} disabled={isLoading}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Reset Defaults
              </Button>
              <Button onClick={() => handleSaveConfig('Classification')} disabled={isLoading}>
                {isLoading ? (
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                ) : showSuccess ? (
                  <CheckCircle className="mr-2 h-4 w-4 text-green-500" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Changes
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        {/* AI Models Tab */}
        <TabsContent value="ai" className="space-y-4 animate-in fade-in-50">
          <Card>
            <CardHeader>
              <CardTitle>AI Model Configuration</CardTitle>
              <CardDescription>
                Configure AI model settings and defaults.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="default_model">Default AI Model</Label>
                  <Select
                    value={config.default_model}
                    onValueChange={(value) => setConfig({
                      ...config,
                      default_model: value
                    })}
                  >
                    <SelectTrigger id="default_model">
                      <SelectValue placeholder="Select a model" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="claude-3-5-sonnet-20241022">Claude 3.5 Sonnet</SelectItem>
                      <SelectItem value="claude-3-opus-20240229">Claude 3 Opus</SelectItem>
                      <SelectItem value="claude-3-haiku-20240307">Claude 3 Haiku</SelectItem>
                      <SelectItem value="claude-3-7-sonnet-20250219">Claude 3.7 Sonnet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Switch id="enable_caching" defaultChecked />
                      <Label htmlFor="enable_caching">Enable response caching</Label>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cache_ttl">Cache TTL (seconds)</Label>
                    <Input
                      id="cache_ttl"
                      type="number"
                      defaultValue="3600"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline" onClick={() => handleResetConfig('AI Models')} disabled={isLoading}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Reset Defaults
              </Button>
              <Button onClick={() => handleSaveConfig('AI Models')} disabled={isLoading}>
                {isLoading ? (
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                ) : showSuccess ? (
                  <CheckCircle className="mr-2 h-4 w-4 text-green-500" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Changes
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="space-y-4 animate-in fade-in-50">
          <Card>
            <CardHeader>
              <CardTitle>Security Settings</CardTitle>
              <CardDescription>
                Configure security and access control settings.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="api_url">API URL</Label>
                  <Input
                    id="api_url"
                    value={config.api_url}
                    onChange={(e) => setConfig({
                      ...config,
                      api_url: e.target.value
                    })}
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Switch id="enable_cors" defaultChecked />
                    <Label htmlFor="enable_cors">Enable CORS</Label>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    When enabled, the API will accept cross-origin requests.
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Switch id="require_auth" defaultChecked />
                    <Label htmlFor="require_auth">Require authentication for all endpoints</Label>
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline" onClick={() => handleResetConfig('Security')} disabled={isLoading}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Reset Defaults
              </Button>
              <Button onClick={() => handleSaveConfig('Security')} disabled={isLoading}>
                {isLoading ? (
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                ) : showSuccess ? (
                  <CheckCircle className="mr-2 h-4 w-4 text-green-500" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Changes
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        {/* System Tab */}
        <TabsContent value="system" className="space-y-4 animate-in fade-in-50">
          <Card>
            <CardHeader>
              <CardTitle>System Information</CardTitle>
              <CardDescription>
                View system status and resource utilization.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium mb-2">Environment Details</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Environment:</span>
                      <Badge>{config.environment}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">API URL:</span>
                      <span>{config.api_url}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Default Model:</span>
                      <span>{config.default_model}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Debug Mode:</span>
                      <Badge variant={config.system.debug_mode ? "default" : "outline"}>
                        {config.system.debug_mode ? "Enabled" : "Disabled"}
                      </Badge>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium mb-2">Performance Settings</h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="max_workers">Max Workers</Label>
                        <span className="text-sm text-muted-foreground">{config.system.max_workers}</span>
                      </div>
                      <Slider
                        id="max_workers"
                        min={1}
                        max={16}
                        step={1}
                        value={[config.system.max_workers]}
                        onValueChange={(value) => setConfig({
                          ...config,
                          system: { ...config.system, max_workers: value[0] }
                        })}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="processing_timeout">Processing Timeout (seconds)</Label>
                      <Input
                        id="processing_timeout"
                        type="number"
                        value={config.system.processing_timeout}
                        onChange={(e) => setConfig({
                          ...config,
                          system: { ...config.system, processing_timeout: parseInt(e.target.value) }
                        })}
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="enable_monitoring"
                          checked={config.system.enable_monitoring}
                          onCheckedChange={(checked) => setConfig({
                            ...config,
                            system: { ...config.system, enable_monitoring: checked }
                          })}
                        />
                        <Label htmlFor="enable_monitoring">Enable Monitoring</Label>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="debug_mode"
                          checked={config.system.debug_mode}
                          onCheckedChange={(checked) => setConfig({
                            ...config,
                            system: { ...config.system, debug_mode: checked }
                          })}
                        />
                        <Label htmlFor="debug_mode">Debug Mode</Label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
               <div className="pt-4">
                <h3 className="text-lg font-medium mb-2">System Actions</h3>
                <div className="flex flex-wrap gap-4">
                  <Button variant="outline" onClick={() => handleResetConfig('System')}>
                    Reset to Defaults
                  </Button>
                  <Button variant="secondary" onClick={() => toast({ title: "System Action", description: "Clearing cache..." })}>
                    Clear Cache
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => setIsResetDialogOpen(true)}
                    disabled={isLoading}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Reset All Data
                  </Button>
                </div>

                {resetSuccess && (
                  <Alert className="mt-4 bg-green-50 text-green-700 border-green-200">
                    <CheckCircle className="h-4 w-4" />
                    <AlertDescription>Data reset successfully.</AlertDescription>
                  </Alert>
                )}

                {resetError && (
                  <Alert variant="destructive" className="mt-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{resetError}</AlertDescription>
                  </Alert>
                )}
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline" onClick={() => handleResetConfig('System')} disabled={isLoading}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Reset Defaults
              </Button>
              <Button onClick={() => handleSaveConfig('System')} disabled={isLoading}>
                {isLoading ? (
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                ) : showSuccess ? (
                  <CheckCircle className="mr-2 h-4 w-4 text-green-500" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Changes
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        {/* New Health Tab */}
        <TabsContent value="health" className="space-y-4 animate-in fade-in-50">
          <SystemHealthPanel />
        </TabsContent>
      </Tabs>
       <Dialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset System Data</DialogTitle>
            <DialogDescription>
              <div className="space-y-4">
                <div className="text-red-600 font-medium">
                  Warning: This action cannot be undone.
                </div>
                <div>
                  This will permanently delete all data from:
                </div>
                <ul className="list-disc pl-8 space-y-1">
                  <li>Knowledge Graph</li>
                  <li>Vector Store</li>
                  <li>Database Collections</li>
                  <li>File Storage</li>
                </ul>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsResetDialogOpen(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDataReset}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Resetting...
                </div>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Reset All Data
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConfigurationInterface;
